<template>
    <div class="media-popup-container">
        <popup-modal @setAction="handleSetAction" @cancelAction="handleCancelAction">
            <div class="popup-wrapper">
                <p class="title">Media</p>
                <p class="subtitle">Add photos and videos that capture your venue's vibe.</p>
                <div v-if="medias.length" class="carousel-wrapper">
                    <Carousel :mediaList="medias" @currentMediaIndx="updateCurrentMediaIndx" />
                    <button
                        v-if="medias.length"
                        :style="customUploadBtnStyle"
                        @click="handleUploadClick"
                        class="add-medias-btn"
                    ></button>
                    <button
                        v-if="medias.length"
                        :style="customAvatarBtnStyle"
                        :disabled="isAvatar"
                        @click="handleSelectAvatar"
                        class="make-avatar-btn"
                    >
                        {{ isAvatar ? 'profile picture' : 'select avatar' }}
                    </button>
                </div>
                <div v-else class="add-new-media">
                    <button @click="handleUploadClick" class="add-media-btn">
                        Add/Select Image
                    </button>
                </div>
                <div v-if="serverErrMsg" class="input-err-msg">{{ serverErrMsg }}</div>
                <input
                    ref="uploadBtn"
                    @change="handleMediaUpload"
                    id="file-upload"
                    type="file"
                    accept="video/*,image/*"
                    hidden="hidden"
                />
            </div>
        </popup-modal>
    </div>
</template>
<script>
    import axios from 'axios'
    import Carousel from '@/components/ui/carousels/Carousel.vue'
    import PopupModal from '@/components/user/login-popups/PopupModal.vue'

    const URLS = {
        user: '/profile/v1/settings/user/',
        fetchMedia: '/profile/v1/user-profile/venue-medias/',
        media: ['/profile/update-media/', '/venue/'],
        avatar: ['/profile/check-main-media/', '/venue/']
    }

    export default {
        name: 'Media',

        components: {
            'popup-modal': PopupModal,
            Carousel
        },

        emits: ['closeModalAction'],

        data: () => ({
            userId: null,
            currentMediaIndx: 0,
            avatar: '',
            medias: [],
            avatarChanged: false,
            serverErrMsg: '',
            styleOffsets: {
                uploadBtn: {
                    twoMedia: '23%',
                    threeMedia: '48%'
                },

                avatarBtn: {
                    twoMedia: '6%',
                    threeMedia: '35%'
                }
            }
        }),

        methods: {
            handleUploadClick() {
                this.$refs.uploadBtn.click()
            },
            handleMediaUpload(e) {
                if (!e.target.files.length) return
                this.$set(this.medias, this.medias.length, e.target.files[0])
            },
            handleSelectAvatar() {
                this.avatar = this.medias[this.currentMediaIndx].name
                this.avatarChanged = true
            },

            updateCurrentMediaIndx(indx) {
                this.currentMediaIndx = indx
            },

            prepMediaData() {
                let formData = new FormData()

                for (let i = 0; i < this.medias.length; i++) {
                    formData.append('files[]', this.medias[i])
                }

                formData.append('_method', 'PUT')
                return formData
            },

            async handleSetAction() {
                if (this.medias.length) {
                    let mediaData = this.prepMediaData()
                    await this.updateMedias(mediaData)

                    if (this.avatarChanged) await this.updateAvatar()

                    this.$emit('closeModalAction')
                } else {
                    this.serverErrMsg =
                        '[422] : You must upload at least one image or video to continue.'
                }
            },

            handleCancelAction() {
                this.medias.splice(0, this.medias.length)
                this.$emit('closeModalAction')
            },

            async updateMedias(data) {
                try {
                    let userResp = await axios.get(this.URLS.user)
                    this.userId = userResp.data.data[0].id

                    let mediaResp = await axios.post(
                        this.URLS.media[0] + this.userId.toString() + this.URLS.media[1],
                        data
                    )
                    return mediaResp
                } catch (err) {
                    this.serverErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`
                    throw err
                }
            },

            async updateAvatar() {
                try {
                    let resp = await axios.get(this.URLS.fetchMedia + this.userId)

                    let data = resp.data.data
                    let safename = this.avatar.substring(0, this.avatar.lastIndexOf('.'))
                    let avatar = data.filter((v) => v.name.includes(safename))

                    if (!avatar.length)
                        throw new Error('User currently has no avatar, avatar update failed.')

                    let avatarResp = await axios.post(
                        this.URLS.avatar[0] + this.userId.toString() + this.URLS.avatar[1],
                        { _method: 'PUT', id: avatar[0].id }
                    )

                    return avatarResp
                } catch (err) {
                    if (err.response) {
                        this.serverErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`
                    } else {
                        this.serverErrMsg = `[${err.name}] : ${err.message}`
                    }

                    throw err
                }
            }
        },

        computed: {
            URLS: () => URLS,

            customUploadBtnStyle: (self) => {
                if (self.medias.length === 1) {
                    return {}
                } else if (self.medias.length === 2) {
                    return { left: `${self.styleOffsets.uploadBtn.twoMedia}` }
                } else if (self.medias.length === 3) {
                    return { left: `${self.styleOffsets.uploadBtn.threeMedia}` }
                } else {
                    return { left: `${self.styleOffsets.uploadBtn.threeMedia}` }
                }
            },

            customAvatarBtnStyle: (self) => {
                if (self.medias.length === 1) {
                    return {}
                } else if (self.medias.length === 2) {
                    return { left: `${self.styleOffsets.avatarBtn.twoMedia}` }
                } else if (self.medias.length === 3) {
                    return { left: `${self.styleOffsets.avatarBtn.threeMedia}` }
                } else {
                    return { left: `${self.styleOffsets.avatarBtn.threeMedia}` }
                }
            },

            isAvatar: (self) => {
                try {
                    if (self.avatar.includes(self.medias[self.currentMediaIndx].name)) {
                        return true
                    } else if (self.avatar === '' && self.currentMediaIndx === 0) {
                        return true
                    } else {
                        return false
                    }
                } catch {
                    return false
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    .popup-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        margin-left: 90px;

        .title {
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 44px;
            font-style: normal;
            font-weight: 500;
            line-height: 110%;
            letter-spacing: -1.76px;
            margin-top: 50px;
            margin-bottom: 0px;
        }

        .subtitle {
            margin-top: 22px;
            margin-bottom: 0px;
            color: #acacac;
            font-family: Instrument Sans;
            font-size: 16px;
            font-style: normal;
            font-weight: 550;
            line-height: 125%;
        }

        .add-new-media {
            margin-top: 27px;
            margin-bottom: 46px;

            button {
                min-width: 317px;
                min-height: 317px;
                border-radius: 10px;
                border: 1.5px solid #8b8b8b;
                background: url('/images/user/venue/sample-media.png'),
                    black 50% / cover no-repeat;
                color: #dedede;
                text-align: center;
                font-family: Instrument Sans;
                font-size: 22px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.22px;
            }
        }

        .add-medias-btn {
            background: url('/images/icons/camera.svg') no-repeat 50% 50% black;
            max-height: 25px;
            max-width: 29px;
            background-size: 29px 25px;
            border: none;
            border-radius: 50%;
            padding: 30px;
            position: relative;
            bottom: 30px;
            left: 41%;
            z-index: 11;
        }

        .make-avatar-btn {
            background: url('/images/icons/checkmark.svg') no-repeat 0% 0% transparent;
            color: white;
            font-family: Instrument Sans;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
            letter-spacing: -0.01em;
            text-align: left;
            border: none;
            padding-left: 55px;
            padding-bottom: 10px;
            background-size: 33.27px 33.27px;
            position: relative;
            z-index: 1;
            bottom: 340px;
            left: 10%;
        }

        .carousel-wrapper {
            margin-top: 27px;
        }
    }
</style>
